import { msalInstance } from "app/integrations/azure/authConfig";

type SessionResult = { created: boolean };
let pending: Promise<SessionResult> | undefined;
let loggingOut = false;
let revocation: Promise<void> | undefined;
const sessionCreatedStorageKey = "allsetra-analytics-session-created";
export const analyticsLogoutStorageKey = "allsetra-analytics-logout";
const readLogoutMarker = () => {
  try {
    return localStorage.getItem(analyticsLogoutStorageKey);
  } catch {
    return null;
  }
};
const initialLogoutMarker = readLogoutMarker();

export const analyticsEnabled =
  process.env.REACT_APP_GRAFANA_ENABLED === "true";

export function analyticsUrl(path: string): string {
  const configured = process.env.REACT_APP_ANALYTICS_BASE_URL;
  try {
    const origin = new URL(configured ?? "");
    if (
      origin.protocol === "https:" &&
      !origin.username &&
      !origin.password &&
      origin.pathname === "/" &&
      !origin.search &&
      !origin.hash
    )
      return `${origin.origin}${path}`;
  } catch {
    // Missing or invalid configuration must never fall back to the portal host.
  }
  throw new Error("Analytics HTTPS origin is not configured.");
}

function checkLogout() {
  if (readLogoutMarker() !== initialLogoutMarker)
    void endAnalyticsSession(false);
  if (loggingOut) throw new Error("Portal session is ending.");
}

if (analyticsEnabled)
  window.addEventListener("storage", (event) => {
    if (
      event.key === analyticsLogoutStorageKey &&
      event.newValue !== initialLogoutMarker
    )
      void endAnalyticsSession(false);
    if (
      event.key === sessionCreatedStorageKey &&
      event.newValue !== null &&
      !loggingOut &&
      readLogoutMarker() === initialLogoutMarker
    )
      window.dispatchEvent(new Event(sessionCreatedStorageKey));
  });

export async function ensureAnalyticsSession(): Promise<SessionResult> {
  const sessionUrl = analyticsUrl("/analytics/session");
  checkLogout();
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("Portal authentication is required.");
  // Always revalidate with the bridge. Its process-local sessions can disappear
  // before expiry after a restart; a browser cache would prevent silent recovery.
  if (pending) return pending;

  const requestedIdentity = account.homeAccountId;
  pending = (async () => {
    // The bridge explicitly validates this portal ID token as a login assertion.
    // This path intentionally has no interactive MSAL fallback.
    const result = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile"],
      account,
      redirectUri: window.location.origin
    });
    checkLogout();
    if (
      loggingOut ||
      msalInstance.getActiveAccount()?.homeAccountId !== requestedIdentity
    )
      throw new Error("Portal identity changed.");
    if (!result.idToken)
      throw new Error("Portal login assertion is unavailable.");

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    const create = () => {
      checkLogout();
      return fetch(sessionUrl, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${result.idToken}`,
          "X-Analytics-Request": "1"
        }
      });
    };
    try {
      let response = await create();
      checkLogout();
      if (response.status === 409 && !loggingOut) response = await create();
      if (!response.ok)
        throw new Error("Analytics session could not be established.");
      const session = await response.json();
      checkLogout();
      if (
        loggingOut ||
        msalInstance.getActiveAccount()?.homeAccountId !== requestedIdentity
      )
        throw new Error("Portal identity changed.");
      const expiry = Date.parse(session.expiresAt);
      if (!Number.isFinite(expiry))
        throw new Error("Analytics session response is invalid.");
      if (session.created === true) {
        try {
          // Share only a fresh notification marker, never an assertion or session handle.
          localStorage.setItem(
            sessionCreatedStorageKey,
            crypto.getRandomValues(new Uint32Array(4)).join("-")
          );
        } catch {
          // This tab can still recover when browser storage is unavailable.
        }
      }
      return { created: session.created === true };
    } finally {
      window.clearTimeout(timeout);
    }
  })();
  try {
    return await pending;
  } finally {
    pending = undefined;
  }
}

export async function revokeAnalyticsSession(): Promise<void> {
  await endAnalyticsSession(true);
}

async function endAnalyticsSession(publish: boolean): Promise<void> {
  if (!analyticsEnabled) return;
  if (revocation) return revocation;
  loggingOut = true;
  // Install the shared promise before notifying listeners, which may also log out.
  revocation = Promise.resolve().then(revokeAfterBootstrap);
  if (publish) {
    try {
      localStorage.setItem(
        analyticsLogoutStorageKey,
        `${Date.now()}:${Math.random()}`
      );
    } catch {
      /* MSAL also requires browser storage. */
    }
  }
  window.dispatchEvent(new Event("allsetra-analytics-logout"));
  return revocation;
}

async function revokeAfterBootstrap(): Promise<void> {
  // Let any already-sent bootstrap finish, then revoke its resulting handle.
  const controller = new AbortController();
  let timeout: number | undefined;
  try {
    // Bootstrap requests have a five-second deadline. An MSAL operation still
    // pending afterwards checks loggingOut before sending anything to the bridge.
    if (pending)
      await Promise.race([
        pending.catch(() => {}),
        new Promise((resolve) => window.setTimeout(resolve, 6000))
      ]);
    timeout = window.setTimeout(() => controller.abort(), 5000);
    await fetch(analyticsUrl("/analytics/session"), {
      method: "DELETE",
      credentials: "include",
      signal: controller.signal,
      headers: { "X-Analytics-Request": "1" }
    });
  } catch {
    // Offline sessions expire after at most 15 minutes; portal logout continues.
  } finally {
    if (timeout !== undefined) window.clearTimeout(timeout);
  }
}
